import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, Crown, Clock, Loader2, Infinity as InfinityIcon, ShieldCheck, Download } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";

export async function startCheckout(lookupKey, eventId) {
  const { data } = await api.post("/payments/checkout", {
    lookup_key: lookupKey,
    event_id: eventId || null,
    origin_url: window.location.origin,
  });
  if (data?.checkout_url) {
    window.location.href = data.checkout_url;
    return true;
  }
  throw new Error("No checkout URL");
}

const KEEPSAKE_BENEFITS = [
  { icon: InfinityIcon, t: "Unlimited tributes & 3-minute clips" },
  { icon: Sparkles, t: "No watermark · full music library" },
  { icon: ShieldCheck, t: "Stored forever + private streaming page" },
  { icon: Download, t: "Unlimited recompiles & clip downloads" },
];

export function UpgradeDialog({ open, onOpenChange, eventId, reason }) {
  const [busy, setBusy] = useState(null);

  const buy = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      await startCheckout(lookupKey, eventId);
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="upgrade-dialog">
        <DialogHeader>
          <div className="h-12 w-12 grid place-items-center rounded-2xl bg-[hsl(35_55%_92%)] text-[hsl(var(--accent))] mb-1"><Crown size={24} /></div>
          <DialogTitle className="font-display text-2xl">Keep this tribute forever</DialogTitle>
          <DialogDescription>
            {reason || "Upgrade to Keepsake to unlock the full experience — a one-time payment, yours to keep."}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl border border-[hsl(var(--border))] p-5">
          <div className="flex items-baseline justify-between">
            <div className="font-display text-lg font-semibold">Keepsake</div>
            <div><span className="font-display text-3xl font-semibold">$29</span><span className="text-sm text-[hsl(var(--muted-foreground))]"> one-time</span></div>
          </div>
          <ul className="mt-4 space-y-2.5">
            {KEEPSAKE_BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <span className="mt-0.5 text-[hsl(var(--accent))]"><Check size={16} /></span>
                <span>{b.t}</span>
              </li>
            ))}
          </ul>
          <button data-testid="buy-keepsake-button" disabled={!!busy} onClick={() => buy("keepsake_one_time")}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
            {busy === "keepsake_one_time" ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />}
            Keep it forever — $29
          </button>
        </div>

        <button data-testid="buy-legacy-link" disabled={!!busy} onClick={() => buy("legacy_one_time")}
          className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors underline underline-offset-2">
          Or go all-in with Legacy — $59 (4K, custom cards & multiple versions)
        </button>
        <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">Secure checkout by Stripe · test mode</p>
      </DialogContent>
    </Dialog>
  );
}

export function PlanBadge({ tier }) {
  if (!tier || tier === "free") {
    return <span data-testid="plan-badge" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">Free · Gather</span>;
  }
  const labels = { keepsake: "Keepsake", legacy: "Legacy", pro: "Pro" };
  return (
    <span data-testid="plan-badge" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[hsl(35_55%_90%)] text-[hsl(var(--accent))]">
      <Crown size={12} /> {labels[tier] || tier}
    </span>
  );
}

function daysHoursLeft(deleteAt) {
  const ms = new Date(deleteAt).getTime() - Date.now();
  if (ms <= 0) return { expired: true, label: "any moment now" };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days >= 1) return { days, label: `${days} day${days === 1 ? "" : "s"}` };
  return { days: 0, label: `${hours} hour${hours === 1 ? "" : "s"}` };
}

export function ExpiryBanner({ deleteAt, onUpgrade }) {
  if (!deleteAt) return null;
  const { label, expired } = daysHoursLeft(deleteAt);
  return (
    <div data-testid="expiry-banner" className="rounded-2xl border border-[hsl(35_50%_80%)] bg-[hsl(35_60%_96%)] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="h-10 w-10 shrink-0 grid place-items-center rounded-xl bg-[hsl(35_55%_88%)] text-[hsl(var(--accent))]"><Clock size={20} /></div>
      <div className="flex-1">
        <p className="font-semibold text-[hsl(24_30%_20%)]">
          {expired ? "Your video is scheduled for deletion" : `Your video will be deleted in ${label}`}
        </p>
        <p className="text-sm text-[hsl(24_20%_35%)]">Keep it forever for $29 — no watermark, unlimited tributes, and a private page for family.</p>
      </div>
      <button data-testid="expiry-upgrade-button" onClick={onUpgrade}
        className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity">
        <Crown size={16} /> Keep it forever
      </button>
    </div>
  );
}
